import { Button, Result, message } from 'antd';
import { getAuth, sendEmailVerification } from 'firebase/auth';
import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

/**
 * VerifyEmailRequired — the full-screen state for the CEL-1148 email-relink identity gate.
 *
 * When an account already exists for this email (Google-only user, invited user, admin-renamed
 * email) and the current session's credential hasn't PROVEN control of the email, the backend
 * auth middleware rejects every API call with error_type=email_verification_required. The
 * shared axios interceptor (services/auth/axiosConfig.js) routes here — same pattern as
 * AccountDeactivated. This page makes NO API calls (they'd all be rejected).
 *
 * The way through: click the verification link (the signup already sent one, and the button
 * below sends a fresh one, CEL-3743: invitees who lost the signup email had no way to get
 * another), then hit "I've verified my email" — it force-refreshes the Firebase ID token so
 * the next requests carry email_verified=true, and reloads. Signing in with Google or
 * completing a password reset also proves ownership.
 *
 * The path /verify-email-required MUST stay on the load balancer allowlist
 * (infra/lb/tradewise-lb.urlmap.yaml; py/cellcog/scripts/validate_lb_routes.py checks it).
 */
const VerifyEmailRequired = () => {
  const { logout } = useAuth();
  const [checking, setChecking] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendDisabled, setResendDisabled] = useState(false);

  const handleVerified = async () => {
    setChecking(true);
    try {
      // Force a fresh ID token so it reflects the just-clicked verification link.
      await getAuth().currentUser?.getIdToken(true);
    } catch (e) {
      // Best-effort — the reload below re-runs the whole auth flow regardless.
    }
    window.location.assign('/');
  };

  const handleResend = async () => {
    const currentUser = getAuth().currentUser;
    if (!currentUser) {
      message.error('Please sign in again to resend the verification email.');
      return;
    }
    setResending(true);
    try {
      await sendEmailVerification(currentUser, { url: window.location.origin + '/' });
      message.success(`Verification email sent to ${currentUser.email}. Check your inbox.`);
      // Same 60 s cooldown as the account-activation screen (AccountBlockedPage).
      setResendDisabled(true);
      setTimeout(() => setResendDisabled(false), 60000);
    } catch (error) {
      if (error?.code === 'auth/too-many-requests') {
        message.error('Too many requests. Please wait a few minutes and try again.');
      } else {
        message.error('Could not send the verification email. Please try again.');
      }
    } finally {
      setResending(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await logout();
    } catch (e) {
      // Session teardown is best-effort — the gate is enforced server-side regardless.
    }
    window.location.assign('/login');
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Result
        status="403"
        title="Verify your email to continue"
        subTitle="An account with this email already exists. Check your inbox for the verification link we sent, then come back — or sign in with Google, or reset your password."
        extra={[
          <Button key="verified" type="primary" loading={checking} onClick={handleVerified}>
            I've verified my email
          </Button>,
          <Button key="resend" loading={resending} disabled={resendDisabled} onClick={handleResend}>
            Resend verification email
          </Button>,
          <Button key="signout" onClick={handleSignOut}>
            Sign out
          </Button>,
        ]}
      />
    </div>
  );
};

export default VerifyEmailRequired;
